import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge, type Tone } from "../../../../components/ui";
import { useBasesLocacionesStore } from "../../basesStore";
import { usePatrolsStore } from "../../patrolsStore";
import type { IncidentStatus, RoundStatus, Severity, CheckpointMethod } from "../../services/patrolsApi";
import type { TreeNode } from "../../components/console";
import { TYPE_ICONS, STATUS_TONES } from "../BasesList";

// Piezas comunes de la sub app Rondines.

export const ROUND_TONES: Record<RoundStatus, Tone> = {
  completed: "success",
  partial: "warning",
  missed: "error",
  in_progress: "info",
  scheduled: "neutral",
};
export const SEVERITY_TONES: Record<Severity, Tone> = { low: "neutral", medium: "info", high: "warning", critical: "error" };
export const INCIDENT_TONES: Record<IncidentStatus, Tone> = { open: "error", in_progress: "warning", resolved: "success", closed: "neutral" };
export const METHOD_ICONS: Record<CheckpointMethod, string> = { qr: "ri-qr-code-line", nfc: "ri-rfid-line", gps: "ri-map-pin-user-line" };

export function RoundBadge({ status }: { status: RoundStatus }) {
  const { t } = useTranslation("bases");
  return (
    <Badge size="sm" tone={ROUND_TONES[status]} dot>
      {t(`rondines.roundStatus.${status}`)}
    </Badge>
  );
}

export function SeverityBadge({ severity, solid = false }: { severity: Severity; solid?: boolean }) {
  const { t } = useTranslation("bases");
  return (
    <Badge size="sm" tone={SEVERITY_TONES[severity]} variant={solid ? "solid" : "soft"}>
      <i className="ri-error-warning-line" /> {t(`rondines.severity.${severity}`)}
    </Badge>
  );
}

export function MethodBadge({ method }: { method: CheckpointMethod }) {
  const { t } = useTranslation("bases");
  return (
    <Badge size="sm" tone="primary">
      <i className={METHOD_ICONS[method]} /> {t(`rondines.method.${method}`)}
    </Badge>
  );
}

/** Árbol bases → rutas (para filtrar cualquier vista de Rondines). */
export function useRouteTree(): TreeNode[] {
  const bases = useBasesLocacionesStore((s) => s.bases);
  const routes = usePatrolsStore((s) => s.routes);
  return useMemo(
    () =>
      bases
        .filter((b) => routes.some((r) => r.base_id === b.id))
        .map((b) => ({
          id: `base:${b.id}`,
          label: b.name,
          icon: TYPE_ICONS[b.type],
          dotTone: STATUS_TONES[b.status],
          count: routes.filter((r) => r.base_id === b.id).length,
          children: routes.filter((r) => r.base_id === b.id).map((r) => ({ id: `route:${r.id}`, label: r.name, icon: "ri-route-line", count: r.checkpointCount })),
        })),
    [bases, routes],
  );
}

/** ¿El elemento (con base_id y route_id) pasa la selección del árbol? */
export function treeMatch(sel: string | null, item: { base_id?: string | null; route_id?: string | null; id?: string }): boolean {
  if (!sel) return true;
  if (sel.startsWith("base:")) return item.base_id === sel.slice(5);
  if (sel.startsWith("route:")) return item.route_id === sel.slice(6) || item.id === sel.slice(6);
  return true;
}

/** Estado de carga combinado de Rondines (rutas + bases). */
export function useRondinesStatus() {
  const status = usePatrolsStore((s) => s.status);
  const error = usePatrolsStore((s) => s.error);
  const hydrate = usePatrolsStore((s) => s.hydrate);
  return { status, error, retry: () => void hydrate() };
}
